import React, { useRef } from 'react';
import {
    Badge, Box, Button, Flex, HStack, Icon, SimpleGrid, Text, Textarea, Tooltip, Wrap, WrapItem,
} from '@chakra-ui/react';
import { FaUndo, FaWarehouse } from 'react-icons/fa';
import { MODELO_PADRAO, problemaDoModelo, textoMotorista, VARIAVEIS } from './texto';

// Exemplo da previa: os mesmos dados que a equipe ve numa coleta de verdade.
const EXEMPLO = {
    ponto: { nome: 'MSAM', instrucoes: 'Retirar no balcão dos fundos' },
    compras: [{ numero_pedido: '1602308', palavra_chave: 'FERRO' }, { numero_pedido: '1602309' }],
    complemento: 'Complemento: Galpão 3',
};

/**
 * Texto padrao do motorista na coleta de compra (#2307). A tela de pedir a
 * coleta abre com este modelo preenchido, e o operador ainda pode editar
 * corrida a corrida.
 */
export default function ConfigTextoColeta({ valor, onChange, padrao = MODELO_PADRAO, variaveis = VARIAVEIS }) {
    const campo = useRef(null);
    const problema = problemaDoModelo(valor);
    const ehPadrao = valor.trim() === padrao;

    // Insere a variavel onde esta o cursor, sem o operador precisar digitar as chaves.
    const inserir = (variavel) => {
        const el = campo.current;
        const inicio = el ? el.selectionStart : valor.length;
        const fim = el ? el.selectionEnd : valor.length;
        onChange(`${valor.slice(0, inicio)}${variavel}${valor.slice(fim)}`);
        requestAnimationFrame(() => {
            if (!el) return;
            el.focus();
            el.setSelectionRange(inicio + variavel.length, inicio + variavel.length);
        });
    };

    return (
        <Box>
            <Flex justify="space-between" align="center" mb={1} gap={2} wrap="wrap">
                <HStack spacing={2}>
                    <Icon as={FaWarehouse} color="gray.400" boxSize={3} />
                    <Text fontSize="sm" fontWeight="bold" color="gray.700">Texto padrão do motorista na coleta de compra</Text>
                </HStack>
                {!ehPadrao && (
                    <Button size="xs" variant="ghost" leftIcon={<Icon as={FaUndo} boxSize={2.5} />} onClick={() => onChange(padrao)}>
                        Restaurar padrão
                    </Button>
                )}
            </Flex>
            <Text fontSize="xs" color="gray.400" mb={3}>
                É o texto que abre preenchido ao pedir a Lalamove nos furos e nas listas de compras (dá para editar em cada corrida).
                Linha que ficar sem valor some do texto.
            </Text>

            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
                <Box>
                    <Textarea
                        ref={campo}
                        value={valor}
                        onChange={(e) => onChange(e.target.value)}
                        rows={7}
                        fontFamily="mono"
                        fontSize="xs"
                        borderRadius="lg"
                        maxLength={1000}
                        isInvalid={!!problema}
                        aria-label="Texto padrão do motorista na coleta de compra"
                    />
                    <Flex justify="space-between" mt={1} gap={2}>
                        <Text fontSize="xs" color={problema ? 'red.500' : 'gray.400'}>{problema || 'Modelo válido'}</Text>
                        <Text fontSize="xs" color="gray.400" flexShrink={0}>{valor.length}/1000</Text>
                    </Flex>
                    <Text fontSize="xs" color="gray.500" fontWeight="bold" mt={3} mb={1}>Variáveis (clique para inserir)</Text>
                    <Wrap spacing={1}>
                        {Object.entries(variaveis).map(([v, ajuda]) => (
                            <WrapItem key={v}>
                                <Tooltip label={ajuda} hasArrow>
                                    <Badge as="button" type="button" onClick={() => inserir(v)} colorScheme={v === '{pedidos}' ? 'purple' : 'gray'}
                                        variant="subtle" fontFamily="mono" fontSize="10px" textTransform="none" px={2} py={0.5} borderRadius="md" cursor="pointer"
                                        _hover={{ filter: 'brightness(0.95)' }}>
                                        {v}
                                    </Badge>
                                </Tooltip>
                            </WrapItem>
                        ))}
                    </Wrap>
                </Box>

                <Box borderWidth="1px" borderColor="gray.100" bg="gray.50" borderRadius="lg" p={3}>
                    <Text fontSize="10px" color="gray.500" fontWeight="bold" textTransform="uppercase" mb={2}>
                        Prévia (exemplo: 2 pedidos da MSAM)
                    </Text>
                    <Text fontSize="xs" fontFamily="mono" whiteSpace="pre-wrap" color={problema ? 'gray.400' : 'gray.800'}>
                        {textoMotorista({ modelo: valor, ...EXEMPLO })}
                    </Text>
                </Box>
            </SimpleGrid>
        </Box>
    );
}
